// hooks/useAccessControl.js
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import useAxios from "../utils/useAxios";
import { permissionsKey, retryPermissionsRequest } from "../utils/permissionKeys";

const DEFAULT_PERMISSIONS = {
  can_view: false,
  can_add: false,
  can_edit: false,
  can_delete: false,
};

export const useAccessControl = (contentType, objectId, options = {}) => {
  const api = useAxios();
  const { enabled = true, lazy = false } = options;

  const shouldFetch = Boolean(
    enabled && !lazy && api && contentType && objectId,
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: permissionsKey(contentType, objectId),
    enabled: shouldFetch,
    queryFn: async ({ signal }) => {
      const response = await api.get(
        `/get-user-permissions/${contentType}/${objectId}/`,
        { signal },
      );
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: retryPermissionsRequest,
    // Return cached data immediately while revalidating
    placeholderData: (previousData) => previousData,
  });

  const permissions = useMemo(
    () => data?.permissions || DEFAULT_PERMISSIONS,
    [data?.permissions],
  );

  return {
    permissions,
    accessLevel: data?.access_level ?? null,
    loading: isLoading,
    error,
    canView: permissions.can_view,
    canEdit: permissions.can_edit,
    canDelete: permissions.can_delete,
    canAdd: permissions.can_add,
    canShare: permissions.can_delete, // Assuming share = delete permission
    // Trigger fetch manually if lazy
    loadPermissions: lazy ? refetch : undefined,
  };
};